'use client'

import { CSSProperties, useEffect, useRef, useState } from 'react'
import { useFrequencyData } from '../../utils/useFrequencyData'
import { processAudioFftValue } from './audioUtils'
import { drawSize } from './drawSize'

interface BarsProps {
  values: number[]
  width?: number
  height?: number
  lineThickness?: number
  gapSize?: number
  roundness?: number
  placement?: 'over' | 'under' | 'middle'
  color?: CSSProperties['color']
  maxAmplitude?: number
}

const Bars: React.FC<BarsProps> = ({
  values,
  width = 400,
  height = 100,
  lineThickness = 8,
  gapSize = 8,
  roundness = 4,
  placement = 'middle',
  color = 'white',
  maxAmplitude = 1,
}) => {
  const w = width
  const h = height

  if (!values || values.length === 0) {
    return null
  }

  return (
    <div style={{ width, height }}>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        {values.map((v, i) => {
          const barHeight = (v * h) / maxAmplitude
          const x = i * (lineThickness + gapSize)
          const y =
            placement === 'over'
              ? 0
              : placement === 'under'
                ? h - barHeight
                : 0.5 * (h - barHeight)

          return (
            <rect
              key={i}
              fill={color}
              x={x}
              y={y}
              width={lineThickness}
              height={barHeight}
              rx={roundness}
            />
          )
        })}
      </svg>
    </div>
  )
}

export const BarsVisualization: React.FC<
  Omit<BarsProps, 'values'> & {
    className?: string
    maxDb?: number | undefined
    mediaStart?: number | undefined
    mediaUrl: string
    minDb?: number | undefined
    mirror?: boolean | undefined
    waveformUrl?: string | null | undefined
  }
> = ({
  className,
  color = 'white',
  gapSize = 6,
  height,
  lineThickness = 4,
  maxDb,
  mediaStart,
  mediaUrl,
  minDb,
  mirror,
  placement = 'middle',
  roundness = 2,
  waveformUrl,
  width,
}) => {
  const frequencyData = useFrequencyData(mediaUrl, mediaStart, waveformUrl)
  const containerRef = useRef<HTMLDivElement>(null)
  const [measured, setMeasured] = useState({ width, height })

  useEffect(() => {
    setMeasured((sz) => ({
      width: containerRef.current?.offsetWidth ?? sz.width,
      height: containerRef.current?.offsetHeight ?? sz.height,
    }))
  }, [])
  const size = drawSize({ width, height }, measured)

  return (
    <div ref={containerRef} style={{ width, height }} className={className}>
      {!!frequencyData && !!size.height && !!size.width && (
        <Bars
          width={size.width}
          height={size.height}
          values={getBars({
            totalWidth: size.width,
            itemWidth: lineThickness + gapSize,
            frequencyData: mirror
              ? // Copy before reversing: `Array.prototype.reverse` mutates, and
                // `frequencyData` is the hook's memoized array.
                [...frequencyData, ...[...frequencyData].reverse()]
              : frequencyData,
            maxDb,
            minDb,
          })}
          lineThickness={lineThickness}
          gapSize={gapSize}
          roundness={roundness}
          color={color}
          placement={placement}
        />
      )}
    </div>
  )
}

export const getBars = ({
  totalWidth,
  itemWidth,
  frequencyData,
  maxDb = -50,
  minDb = -80,
}: {
  totalWidth: number
  itemWidth: number
  frequencyData: number[]
  maxDb?: number | undefined
  minDb?: number | undefined
}) => {
  const nBars = Math.floor(totalWidth / itemWidth)
  const samples = frequencyData
  // At least 1. When `nBars` exceeds the number of samples the floor is 0, and
  // every bar then reads `samples[0]` — a row of identical bars that ignores
  // the audio entirely.
  const sampleStep = Math.max(1, Math.floor(samples.length / nBars))

  const bars = Array.from({ length: nBars }).map((_, i) => {
    const processed = processAudioFftValue(
      samples[(i * sampleStep) % samples.length]!,
      { maxDb, minDb },
    )

    return Math.log(1 + processed)
  })

  return bars
}
